import { Typography } from '@mui/material';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CallDaoService } from 'features/call/call-base/services';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { handleResetCall } from 'features/call/call-base/store';
import { callIdSelector } from 'features/call/call-base/store/selectors';
import { isPrimaryHearingConnectedSelector } from 'features/call/call-hearing/store';
import { ColdHandoffStatus } from 'features/cold-handoff/enums';
import { useColdHandoffAcceptedRnsHandlers } from 'features/cold-handoff/hooks';
import { ColdHandoffDaoService } from 'features/cold-handoff/services';
import {
  coldHandoffAcceptingUserUriSelector,
  coldHandoffStatusSelector,
} from 'features/cold-handoff/store';
import { CollaborationStepContainer } from 'features/collaboration-base/components';
import { handleError } from 'features/notification/store';
import { sessionIdSelector } from 'features/session/store';
import { RtcSessionTerminateCauses } from 'features/sip/enums';
import { SipSessionEventBus } from 'features/sip/services';
import { disableSpawnCall } from 'features/spawn-call/store';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { Button } from 'features/ui/elements';
import { resetVriChat } from 'features/vri-chat/store';
import { VoiceMeetingEventBus } from 'features/voice-meeting/services';

export const ColdHandoffAccepted: FC = () => {
  const { t: ct } = useTranslation();
  const dispatch = useAppDispatch();
  const callId = useAppSelector(callIdSelector);
  const sessionId = useAppSelector(sessionIdSelector);
  const status = useAppSelector(coldHandoffStatusSelector);
  const acceptingUserUri = useAppSelector(coldHandoffAcceptingUserUriSelector);
  const isPrimaryHearingConnected = useAppSelector(
    isPrimaryHearingConnectedSelector
  );
  useColdHandoffAcceptedRnsHandlers();

  const handleConfirmRequest = useCallback(async () => {
    try {
      SipSessionEventBus.refer$.next(acceptingUserUri);

      await ColdHandoffDaoService.transferHandoff({
        callId,
        sessionLoginHistoryId: sessionId,
      });

      await CallDaoService.interpretUpdate(callId);

      SipSessionEventBus.terminate$.next({
        cause: RtcSessionTerminateCauses.HANDOFF,
      });
      VoiceMeetingEventBus.resetVoiceSession$.next();

      dispatch(handleResetCall()).unwrap();
      dispatch(disableSpawnCall());
      dispatch(resetVriChat());
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'handleConfirmRequest',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, isPrimaryHearingConnected, acceptingUserUri, sessionId]);

  return (
    <>
      {status === ColdHandoffStatus.ACCEPTED && (
        <CollaborationStepContainer testId='cold-handoff-accepted'>
          <Typography variant='body1' color={theme.palette.grey[50]}>
            {ct('availableVi')}
          </Typography>
          <Typography
            variant='body2'
            color={theme.palette.common.white}
            mt={8}
            lineHeight='24px'
          >
            {ct('handoffCallDescription')}
          </Typography>
          <ConfirmButton
            size='small'
            color='primary'
            title={ct('handoffCall')}
            onClick={handleConfirmRequest}
            autoFocus
          >
            {ct('handoffCall')}
          </ConfirmButton>
        </CollaborationStepContainer>
      )}
    </>
  );
};

const ConfirmButton = styled(Button)`
  margin-top: ${theme.spacing(8)};
`;
